<template>

  <ui-card name="overview.transport_distribution" :is-loading="isLoading" :error="error" padded>
    <ui-day-filter slot="controls" />

    <grouped-bar-chart v-if="data && data.length > 0" :data="data" />

    <div v-else-if="isLoading" class="blokk-chart">
      <div class="blokk shimmer blokk-legend">
        <div class="blokk-legend-item">
          <div class="blokk-legend-item-dot"></div>
          <div class="blokk-legend-item-text">----</div>
        </div>
        <div class="blokk-legend-item">
          <div class="blokk-legend-item-dot"></div>
          <div class="blokk-legend-item-text">----</div>
        </div>
        <div class="blokk-legend-item">
          <div class="blokk-legend-item-dot"></div>
          <div class="blokk-legend-item-text">----</div>
        </div>
        <div class="blokk-legend-item">
          <div class="blokk-legend-item-dot"></div>
          <div class="blokk-legend-item-text">----</div>
        </div>
        <div class="blokk-legend-item">
          <div class="blokk-legend-item-dot"></div>
          <div class="blokk-legend-item-text">----</div>
        </div>
      </div>
      <div class="blokk-y-axis">
        <div class="blokk-row">
          <div class="blokk shimmer is-extra-small blokk-text">--</div>
          <div class="blokk-line"></div>
        </div>
        <div class="blokk-row">
          <div class="blokk shimmer is-extra-small blokk-text">--</div>
          <div class="blokk-line"></div>
        </div>
        <div class="blokk-row">
          <div class="blokk shimmer is-extra-small blokk-text">--</div>
          <div class="blokk-line"></div>
        </div>
      </div>
      <div class="blokk-x-axis">
        <div class="blokk shimmer blokk-text">
          <div>--------</div>
          <div>---------</div>
          <div>-------</div>
        </div>
        <div class="blokk-line">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    </div>

  </ui-card>

</template>

<script>
import { filter, has, zipObject, uniq } from 'lodash';
import { map, sortBy, flow } from 'lodash/fp';
import GroupedBarChart from 'views/charts/GroupedBarChart';
import { card } from 'views/mixins';

export default {
  extends: card('filter-dates', 'filter-days'),

  components: {
    GroupedBarChart,
  },

  methods: {
    makeQuery(params) {
      return {
        app_id: params.appId,
        transformations: [],
        sets: [
          {
            store: 'transports',
            measures: [
              {
                measure: 'nr_users',
                agg: 'count',
                custom_id: 'nr_users',
              },
            ],
            dimensions: [
              {
                dim: 'transport_mode',
                custom_id: 'transport_mode',
              },
              {
                dim: 'part_of_day',
                custom_id: 'part_of_day',
              },
            ],
            filters: [
              {
                scope: 'req',
                type: 'range',
                dim: 'isotime',
                begin: params.from,
                end: params.to,
              },
              {
                scope: 'req',
                type: 'values',
                dim: 'day_of_week',
                values: params.domain,
              },
              {
                scope: 'req',
                type: 'values',
                dim: 'transport_mode',
                values: ['car', 'walking', 'biking', 'train', 'bus'],
              },
            ],
          },
          {
            store: 'transports',
            measures: [
              {
                measure: 'nr_users',
                agg: 'count',
              },
            ],
            dimensions: [],
            filters: [
              {
                scope: 'req',
                type: 'range',
                dim: 'isotime',
                begin: params.from,
                end: params.to,
              },
            ],
          },
        ],
      };
    },

    parseData(counts, total) {
      if (!has(total, 'rows[0][0]')) {
        return;
      }

      // sorting
      const preferredOrder = ['morning', 'afternoon', 'evening', 'night'];
      const sortByType = function(o) {
        return preferredOrder.indexOf(o);
      };

      // variables
      const totalUsers = total.rows[0][0];

      let fields = counts.fields;
      let rows = counts.rows;

      fields = map(field => field.custom_id)(fields);
      rows = map(x => {
        const item = zipObject(fields, x);

        return {
          part_of_day: item.part_of_day,
          mode: item.transport_mode,
          value: item.nr_users / totalUsers,
        };
      })(rows);

      const categories = flow(
        map(row => row.part_of_day),
        uniq,
        sortBy(sortByType)
      )(rows);

      const data = map(category => ({
        category: category,
        values: filter(rows, row => category === row.part_of_day),
      }))(uniq(categories));

      return data;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.card-content {
  padding-top: 1rem;
}

.content {
  height: 100%;
}

.grouped-bar-chart {
  height: 100%;
}

.blokk-chart {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding-right: 1px;
}

.blokk-legend {
  display: flex;
  flex-wrap: wrap;
}

.blokk-legend-item {
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  margin-right: 1.625rem;

  &:last-child {
    margin-right: 0;
  }
}

.blokk-legend-item-dot {
  width: 0.375rem;
  height: 0.375rem;
  background-color: $grey-lighter;
  transform: rotate(45deg);
}

.blokk-legend-item-text {
  margin-left: 0.625rem;
  font-size: 0.875rem;
  line-height: 1.14;
}

.blokk-x-axis {
  padding-left: 2.1875rem;

  .blokk-text {
    display: flex;
    justify-content: space-around;
    font-size: $size-small;
  }

  .blokk-line {
    display: flex;
    justify-content: space-between;

    > div {
      width: 1px;
      height: 0.5rem;
      margin-top: -0.5rem;
      transform: translateY(-1.6875rem);
      background-color: $grey-darker;
      opacity: 0.4;
    }
  }
}

.blokk-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  padding-top: 1.0625rem;
  padding-bottom: 0.375rem;

  .blokk-row {
    display: flex;
    align-items: center;
  }

  .blokk-text {
    flex: 0 0 2.1875rem;
    font-size: $size-small;
    line-height: 1.17;
  }

  .blokk-line {
    flex: 1;
    height: 1px;
    background-color: $grey-darker;
    opacity: 0.2;
  }
}
</style>